import { createFileRoute, Link } from '@tanstack/react-router';
import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CashFlowForecastResponse } from '@accounting/shared';
import { CONSOLIDATED, useActiveSelection } from '../../stores/useCompanyStore';
import { api } from '../../lib/api';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/reports/cash-flow-forecast')({
  component: CashFlowForecastReport,
});

function CashFlowForecastReport() {
  const selection = useActiveSelection();
  const consolidated = selection === CONSOLIDATED;
  const [weeks, setWeeks] = useState(13);
  const [startDate, setStartDate] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const { data, isLoading, error } = useQuery({
    queryKey: ['cash-flow-forecast', selection ?? 'none', startDate, weeks],
    enabled: !!selection,
    queryFn: () => {
      const base = consolidated
        ? '/consolidated/reports/cash-flow-forecast'
        : `/companies/${selection}/reports/cash-flow-forecast`;
      const params: Record<string, string> = { weeks: String(weeks) };
      if (startDate) params.startDate = startDate;
      return api.get<CashFlowForecastResponse>(base, params);
    },
  });

  if (!selection) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Cash-Flow Forecast</h1>
        <p className="muted">Pick a company — or “Consolidated” — in the switcher.</p>
      </div>
    );
  }

  const toggle = (i: number) =>
    setExpanded((p) => {
      const n = new Set(p);
      n.has(i) ? n.delete(i) : n.add(i);
      return n;
    });

  const negStyle = (v: string) => (parseFloat(v) < 0 ? { color: 'var(--bad, #C23B22)' } : undefined);

  return (
    <div>
      <Link to="/reports" style={{ fontSize: 13 }}>← Reports</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>
          Cash-Flow Forecast {consolidated && <span className="tag" style={{ marginLeft: 8 }}>Consolidated</span>}
        </h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 13 }}>Start <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></label>
          <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}>
            <option value={13}>13 weeks</option>
            <option value={26}>26 weeks</option>
            <option value={52}>52 weeks</option>
          </select>
        </div>
      </div>

      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Projects cash forward from current bank balances using open invoices (by due date), open
        bills, scheduled loan payments, and recurring entries.
      </p>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load the forecast.</p>}

      {data && (
        <>
          <div className="card" style={{ padding: 16, marginBottom: 16, display: 'flex', gap: 32, flexWrap: 'wrap' }}>
            <Figure label="Starting cash" value={data.startingCash} />
            <Figure label="Expected in" value={data.totalInflow} />
            <Figure label="Expected out" value={`-${data.totalOutflow}`} />
            <Figure label={`Ending cash (${data.weeks}wk)`} value={data.endingCash} strong />
          </div>

          <div className="card" style={{ padding: 16, overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Week of</th>
                  <th style={{ textAlign: 'right' }}>In</th>
                  <th style={{ textAlign: 'right' }}>Out</th>
                  <th style={{ textAlign: 'right' }}>Net</th>
                  <th style={{ textAlign: 'right' }}>Ending balance</th>
                </tr>
              </thead>
              <tbody>
                {data.buckets.map((b, i) => (
                  <Fragment key={b.weekStart}>
                    <tr style={{ cursor: b.items.length ? 'pointer' : 'default' }} onClick={() => b.items.length && toggle(i)}>
                      <td>{b.items.length > 0 ? (expanded.has(i) ? '▾ ' : '▸ ') : ''}{formatAccountingDate(b.weekStart)}</td>
                      <td style={{ textAlign: 'right' }}>{parseFloat(b.inflow) ? formatCurrency(b.inflow) : '—'}</td>
                      <td style={{ textAlign: 'right' }}>{parseFloat(b.outflow) ? `(${formatCurrency(b.outflow)})` : '—'}</td>
                      <td style={{ textAlign: 'right', ...negStyle(b.net) }}>{formatCurrency(b.net)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, ...negStyle(b.endingBalance) }}>{formatCurrency(b.endingBalance)}</td>
                    </tr>
                    {expanded.has(i) && b.items.map((it, j) => (
                      <tr key={`${b.weekStart}-${j}`} style={{ background: 'var(--bg-surface-hover)' }}>
                        <td style={{ paddingLeft: 24, fontSize: 13 }} colSpan={3}>
                          <span className="muted">{formatAccountingDate(it.date)}</span> · {it.label}{' '}
                          <span className="tag" style={{ marginLeft: 4 }}>{it.kind}</span>
                        </td>
                        <td colSpan={2} style={{ textAlign: 'right', fontSize: 13, ...negStyle(it.amount) }}>{formatCurrency(it.amount)}</td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Figure({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  const neg = parseFloat(value) < 0;
  return (
    <div>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: strong ? 22 : 18, fontWeight: strong ? 700 : 600, color: neg ? 'var(--bad, #C23B22)' : undefined }}>
        {formatCurrency(value)}
      </div>
    </div>
  );
}
